import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "../styles/components/OrderHistory.css";

const MONTH_NAMES = [
  "Januar",
  "Februar",
  "Mars",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Desember",
];

const AdminReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
  });
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then(setAccessToken);
      if (user?.email) setUserEmail(user.email);
    }
  }, [isAuthenticated, getAccessTokenSilently, user]);

  const formatMonth = (dateStr) => {
    const d = new Date(dateStr);
    return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  };

  const fetchReports = useCallback(async () => {
    if (!accessToken || !userEmail) return;
    try {
      setLoading(true);
      const response = await fetch(
        `${API_URL}/admin/reports?month=${selectedMonth}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-User-Email": userEmail,
          },
        }
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setReports(data.reports || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching admin reports:", err);
      setError("Kunne ikke hente rapporter. Prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  }, [API_URL, accessToken, userEmail, selectedMonth]);

  const downloadReport = async (reportId, email, month) => {
    try {
      const response = await fetch(`${API_URL}/admin/reports/${reportId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "X-User-Email": userEmail,
        },
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `rapport_${email}_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading report:", err);
      alert("Kunne ikke laste ned rapport. Prøv igjen senere.");
    }
  };

  useEffect(() => {
    if (accessToken && userEmail) fetchReports();
  }, [accessToken, userEmail, fetchReports]);

  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1 - i);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    return { value, label };
  });

  return (
    <div className="order-history">
      <h1>Månedlige rapporter</h1>

      <div
        style={{
          marginBottom: "1.5rem",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <label
          htmlFor="report-month"
          style={{ fontWeight: 600, color: "var(--main)" }}
        >
          Velg måned:
        </label>
        <select
          id="report-month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            padding: "0.4rem 0.75rem",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontSize: "1rem",
            color: "var(--main)",
          }}
        >
          {monthOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading">Laster inn rapporter...</div>
      ) : error ? (
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchReports} className="retry-btn">
            Prøv på nytt
          </button>
        </div>
      ) : reports.length === 0 ? (
        <div className="no-orders">
          <p>Ingen rapporter funnet for valgt måned.</p>
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Bruker</th>
                <th>Periode</th>
                <th>Generert</th>
                <th>Handling</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>{report.email}</td>
                  <td>{formatMonth(report.month)}</td>
                  <td>
                    {new Date(report.created_at).toLocaleDateString("nb-NO")}
                  </td>
                  <td className="order-action">
                    <button
                      className="download-btn"
                      onClick={() =>
                        downloadReport(
                          report.id,
                          report.email,
                          report.month.slice(0, 7)
                        )
                      }
                    >
                      Last ned PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
